/**
 * HeaderSearch — the wall's search, the one ordering that lives in the chrome (BRIEF §14: search in
 * the header, never a band above the wall, never docking on scroll). It sits in `@core/wall/ui`
 * rather than in one product's header folder because every house's header mounts the same block:
 * Toile's `Header` is the first shell to carry it, Narcis and Ovid carry more nav doors beside it.
 *
 * The field holds tokens and the words typed after them (plan `2026_11_01_wall_search_panel.md`, boards 17 and
 * 18): the pills, yours and applied (`"indigo dye"` phrases and `key:value` facet tokens in the query,
 * `@core/wall/tsQuery`'s `parseQuery`); then the lean, the system's guess, outlined; then the input, which keeps the
 * words `and…` after the last token so the field is never closed. A token is removed whole by one click; Backspace at
 * the start of the words marks the last token, a second Backspace removes it (the guard against a stray key); ← and →
 * walk them. A pill is made only by a tap on a chip, a suggestion or an offered value: a quote typed into the field is
 * dropped. Removing a lean token is the caller's (`steer.onLeanDrop`): the lean is never in the query.
 *
 * With `steer` the field opens a panel (`SearchPanel`) on focus: under the field on a desktop, over the screen on
 * the sheet surface, hanging from the top (half sheet) once a pill is set. Without it (a page that has no wall under
 * it, a page rendered to static HTML) the field is the plain field it was: tokens, words, Enter.
 *
 * "The column shows state; tools unfold at their verb" (board `ColumnFurniture`). On a phone a house that keeps
 * `phone: 'fold'` has the field fold to its verb, a glyph, and unfold across the whole header row when tapped; the
 * shell hides its wordmark and nav for as long as `onExpandedChange` reports the field open. Collapsing is chrome,
 * never a search reset: ×, Escape and blur-with-an-empty-field all leave `value` untouched there.
 *
 * Which of the shapes is on screen is decided in CSS, not here: the component renders them all and
 * `HeaderSearch.module.scss` reveals one under `[data-wall-surface="sheet"]` — the same surface `Wall.tsx`'s
 * `isSheetSurface()` decides and publishes on `<html>`. The few decisions that are behaviour (a pill set on a phone
 * puts the keyboard down) read the same attribute when they happen.
 *
 * `phone` — Toile passes `'open'`: on the sheet surface the field stands open at rest, exactly as it does on the column,
 * and the glyph never renders at all (a magnifier reads "filter"; the wall's thesis is "make"). Defaults to `'fold'`,
 * unchanged for a caller that has not opted in (edge case 24).
 *
 * The clearing × (`.clear`) renders whenever `value` is non-empty, at every width, in either mode, and it clears
 * rather than folds ("× is close, not clear," 2026-08-31).
 */
import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import {Notice} from '@core/ui/Notice'
import {MAX_DIRECTION_CHARS, type WallSearchFrom} from '@core/wall/contract'
import type {FacetOffer} from '@core/wall/facetOffer'
import type {HouseFacets} from '@core/wall/facets'
import type {SuggestRow} from '@core/wall/suggest'
import {composeQuery, parseQuery} from '@core/wall/tsQuery'

import {SearchPanel, type PanelLayout} from './SearchPanel'
import {FieldTokenView, OfferedChip} from './SearchTokens'
import {
    dragOutcome, enterAction, enterKeyHint, leanShown, makeRow, panelBody, removal, takeClosest, takeSuggestion, trackEdges,
    viaOf, withToken, yoursOf,
    type FieldToken, type MakeVia,
} from './searchModel'
import {useSuggest} from './useSuggest'

import styles from './HeaderSearch.module.scss'

/**
 * Whether a keydown is the press that only blurs the field (board `FirstGlance`, frame 5 withdrawn
 * 2026-09-18) — Enter, or the phone keyboard's Go (the browser reports both the same way; this
 * field carries no `<form>` for either to submit), outside an IME composition. Exported as a pure
 * predicate so the rule is testable without a DOM (this project runs no jsdom — see this file's own
 * `onKeyDown` for the one call site).
 */
export function isBlurringEnter(key: string, isComposing: boolean): boolean {
    return key === 'Enter' && !isComposing
}

/**
 * What a key does to the tokens before the words, or null when it is the field's own (typing, a caret move inside
 * the words). Backspace at the start marks the last token, a second Backspace removes the marked one; ← at the
 * start (or while one is selected) walks left, → walks right and leaves the tokens past the last. Pure, so the rule
 * is testable without a DOM.
 */
export type PillKeyAction = {kind: 'mark'; index: number | null} | {kind: 'remove'; index: number}

export function pillKeyAction(key: string, atStart: boolean, marked: number | null, count: number): PillKeyAction | null {
    if (count === 0) return null
    if (key === 'Backspace' && atStart) {
        return marked === null ? {kind: 'mark', index: count - 1} : {kind: 'remove', index: marked}
    }
    if (key === 'ArrowLeft' && (atStart || marked !== null)) {
        return {kind: 'mark', index: marked === null ? count - 1 : Math.max(0, marked - 1)}
    }
    if (key === 'ArrowRight' && marked !== null) {
        return {kind: 'mark', index: marked >= count - 1 ? null : marked + 1}
    }
    return null
}

/** How a suggestion or an offered value reached the query (`wall_search.from`). */
export type SearchFrom = Exclude<WallSearchFrom, 'typed'>

/** What the wall gives the field to steer with. Absent, the field is a plain one. */
export type SearchSteer = {
    house: string
    facets: HouseFacets
    /** The share of the house's packs that carry each facet token (`houseFacets.FACET_SHARES[house]`). */
    shares: Record<string, number>
    /** The panel's groups ranked for this visitor and day, as the document was answered with (`facetOffer.ts`). */
    facetOffer?: FacetOffer
    /** The lean, as tokens. */
    lean: FieldToken[]
    recent: string[]
    /** The chips that sit with the pills: those of the matched tiles on screen. */
    withThese: string[]
    /** "wall art dropped" and its Undo, or none. */
    notice: {line: string; onUndo: () => void} | null
    /** Counts the phrases a tap on a tile landed as pills; each bump rings the field's border once. */
    landed: number
    /** A suggestion, an offered value or a Recent row put `q` in the field. */
    onPick: (from: SearchFrom, q: string) => void
    onLeanDrop: (token: FieldToken) => void
    onRecentClear: () => void
    onRecentRemove: (at: number) => void
    /** The Make row, ⌘/Ctrl+Enter or Enter at request length asked for a ghost on the pills and the words. */
    onMake: (via: MakeVia) => void
    /** The field was left with `q` standing: a settled search. */
    onCommit: (q: string) => void
}

export type HeaderSearchProps = {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    /** The field's accessible name — the wall passes its house's (`chromeWords(theme).search`). */
    label?: string
    /** Fires on every fold and unfold of the phone field, so the shell can stand its wordmark and
     *  nav down while the field owns the row. Never fires on desktop, and never at all under
     *  `phone: 'open'`: nothing folds there. */
    onExpandedChange?: (expanded: boolean) => void
    /** Fires as a sheet (full or half) opens and closes over the screen: the shell must not hold the sheet inside its
     *  own box (a blurred or transformed header is the containing block of everything fixed in it). */
    onPanelChange?: (open: boolean) => void
    /** On the sheet surface: `'fold'` (default) collapses the field to its glyph, unfolding across
     *  the row on tap. `'open'` renders the field at rest, no glyph, no fold. Meaningless on the
     *  column surface, where the field is always open regardless. */
    phone?: 'fold' | 'open'
    /** Fires on the blurring Enter/Go with the field's value — for a header mounted on a page that
     *  has no wall under it (Narcis's pack and tool pages), where the press carries the reader back
     *  to the wall with their words. Absent on the wall itself, which steers. */
    onSubmit?: (value: string) => void
    /** A click on a token (or Backspace twice) removed this phrase or facet token; `onChange` has already been given the
     *  query without it. For the wall's event, which the bar's other edits do not raise. */
    onPillRemove?: (phrase: string) => void
    steer?: SearchSteer
    /** A page rendered to static HTML (`scripts/satellites`) has no script to carry `onSubmit`: the
     *  field becomes a plain GET form to `action`, the typed words under the name `q`, `hidden` as
     *  fixed fields beside it. The form is `display: contents`, so the layout is the field's own. */
    form?: {action: string; hidden: Record<string, string>; rel?: string}
}

const NO_FACETS: HouseFacets = []
const RING_MS = 900

/** While the full panel stands the page behind it does not scroll: html and body are held, then given back as they were. */
function usePageLock(active: boolean): void {
    useEffect(() => {
        if (!active) return
        const html = document.documentElement
        const was = {html: html.style.overflow, body: document.body.style.overflow}
        html.style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'
        return () => {
            html.style.overflow = was.html
            document.body.style.overflow = was.body
        }
    }, [active])
}

function onSheetSurface(): boolean {
    return typeof document !== 'undefined' && document.documentElement.getAttribute('data-wall-surface') === 'sheet'
}

function onApple(): boolean {
    return typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent)
}

/** The area the keyboard leaves, on a phone: the sheet is sized to it so its foot stands on the keyboard. */
function useVisualViewport(active: boolean): {top: number; height: number} | null {
    const [box, setBox] = useState<{top: number; height: number} | null>(null)
    useEffect(() => {
        const vv = typeof window === 'undefined' ? null : window.visualViewport
        if (!active || !vv) {
            setBox(null)
            return
        }
        const read = () => setBox({top: vv.offsetTop, height: vv.height})
        read()
        vv.addEventListener('resize', read)
        vv.addEventListener('scroll', read)
        return () => {
            vv.removeEventListener('resize', read)
            vv.removeEventListener('scroll', read)
        }
    }, [active])
    return box
}

const CROSS = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6 L18 18 M18 6 L6 18" />
    </svg>
)

export function HeaderSearch({
    value, onChange, placeholder, label = 'Search', onExpandedChange, onPanelChange, phone = 'fold', onSubmit,
    onPillRemove, steer, form,
}: HeaderSearchProps) {
    const [expanded, setExpanded] = useState(false)
    // A `key:value` word the house has no facet for stays a word under the caret (`re:invent`), as the read path reads it.
    const shares = steer?.shares
    const isFacet = useMemo(() => shares && ((token: string) => shares[token] !== undefined), [shares])
    const parsed = useMemo(() => parseQuery(value, isFacet), [value, isFacet])
    const {words} = parsed
    const facets = steer?.facets ?? NO_FACETS
    const yours = useMemo(() => yoursOf(parsed, facets), [parsed, facets])

    // `mode` is the panel: closed (null), the desktop dropdown, or on the sheet surface the full screen or the half sheet.
    const [mode, setMode] = useState<PanelLayout | null>(null)
    const open = steer !== undefined && mode !== null
    const sheetOpen = mode === 'full' || mode === 'half'
    const make = makeRow(yours, words, {apple: open ? onApple() : true, phone: sheetOpen})
    const lean = steer ? leanShown(steer.lean, make) : []
    const tokens = [...yours, ...lean]
    const body = panelBody(make, words, yours.length)

    // The token a key has marked, by id: Backspace again removes it.
    const [marked, setMarked] = useState<string | null>(null)
    const markedIndex = marked === null ? -1 : tokens.findIndex((t) => t.id === marked)

    const inputRef = useRef<HTMLInputElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const glyphRef = useRef<HTMLButtonElement>(null)
    // Set only by the two deliberate closes (× and Escape). A blur close must not chase the focus
    // the user just moved somewhere else.
    const returnFocusRef = useRef(false)
    const panelId = useId()

    const {rows, fresh: rowsFresh} = useSuggest(value, open)
    const shown = useMemo(
        () => rows.filter((r) => (body === 'closest' ? r.kind === 'closest' : r.kind !== 'closest')),
        [rows, body],
    )
    // The row the keys are on: the first suggestion, or none among the closest, where Enter makes until a key has moved.
    const [current, setCurrent] = useState(0)
    const shownKey = shown.map((r) => r.pill).join('\n')
    useEffect(() => setCurrent(body === 'closest' ? -1 : 0), [shownKey, body])
    const optionId = useCallback((i: number) => `${panelId}-option-${i}`, [panelId])

    const folds = phone === 'fold'
    const hasQuery = value.trim().length > 0
    // The tokens take room out of the one bound the read path keeps on the whole query.
    const wordsRoom = Math.max(0, MAX_DIRECTION_CHARS - (value.length - words.length))
    // `.close` (fold-collapse) already owns this corner while folded-open; the clearing × steps
    // aside there rather than doubling up on the same visual slot.
    const showClear = hasQuery && !(folds && expanded)

    const viewport = useVisualViewport(mode === 'full')
    usePageLock(mode === 'full')

    const openPanel = useCallback(() => {
        if (!steer) return
        setMode((m) => (m === 'dropdown' || m === 'full' ? m : onSheetSurface() ? 'full' : 'dropdown'))
    }, [steer])

    const closePanel = useCallback(() => {
        setMode(null)
        setMarked(null)
    }, [])

    useEffect(() => {
        onPanelChange?.(sheetOpen)
    }, [sheetOpen, onPanelChange])

    // A half sheet hangs from a pill: with none left it has nothing to show, so it rises to the full panel.
    useEffect(() => {
        if (mode === 'half' && yours.length === 0) setMode('full')
    }, [mode, yours.length])

    // The open slot stays in view: tokens that outgrow the field scroll behind it, the slot is the last thing seen.
    useEffect(() => {
        const track = trackRef.current
        if (track) track.scrollLeft = track.scrollWidth
    }, [tokens.length, mode])

    // The track fades at an edge only where it can scroll on.
    const [edges, setEdges] = useState({left: false, right: false})
    const readEdges = useCallback(() => {
        const track = trackRef.current
        if (!track) return
        const next = trackEdges(track.scrollLeft, track.clientWidth, track.scrollWidth)
        setEdges((prev) => (prev.left === next.left && prev.right === next.right ? prev : next))
    }, [])
    useEffect(readEdges, [readEdges, tokens.length, words, mode])
    useEffect(() => {
        const track = trackRef.current
        if (!track || typeof ResizeObserver === 'undefined') return
        const watch = new ResizeObserver(readEdges)
        watch.observe(track)
        return () => watch.disconnect()
    }, [readEdges])

    const removeToken = useCallback(
        (token: FieldToken) => {
            setMarked(null)
            const out = removal(value, token)
            if (out.dropped) {
                steer?.onLeanDrop(out.dropped)
                return
            }
            onChange(out.q)
            onPillRemove?.(token.ref.value)
            if (!sheetOpen) inputRef.current?.focus()
        },
        [steer, onChange, onPillRemove, value, sheetOpen],
    )

    /** A pill landed from the panel: on a phone the keyboard goes down and the panel drops to a half sheet, so the
     *  order can answer in view; on a desktop the panel stays under the caret. */
    const land = useCallback(
        (from: SearchFrom, next: string) => {
            if (next.length > MAX_DIRECTION_CHARS) return
            steer?.onPick(from, next)
            setMarked(null)
            const after = parseQuery(next, isFacet)
            if (onSheetSurface() && (after.pills.length > 0 || after.facets.length > 0)) {
                setMode('half')
                inputRef.current?.blur()
            } else {
                inputRef.current?.focus()
            }
        },
        [steer, isFacet],
    )

    const pickRow = useCallback(
        (row: SuggestRow) => {
            if (row.kind === 'closest') {
                // The request is a settled search before the phrase replaces it.
                steer?.onCommit(value)
                land('phrase', takeClosest(value, row.pill))
                return
            }
            const from: SearchFrom = row.kind === 'facet' ? 'facet' : 'phrase'
            land(from, takeSuggestion(value, {type: row.kind === 'facet' ? 'facet' : 'phrase', value: row.pill}, row.replaces ?? 1))
        },
        [land, value, steer],
    )

    const makeNow = useCallback(
        (via: MakeVia) => {
            inputRef.current?.blur()
            closePanel()
            steer?.onMake(via)
        },
        [steer, closePanel],
    )

    // The landing ring: a bump of `steer.landed` rings the border once.
    const landed = steer?.landed ?? 0
    const [ringing, setRinging] = useState(false)
    useEffect(() => {
        if (landed === 0) return
        setRinging(true)
        const timer = setTimeout(() => setRinging(false), RING_MS)
        return () => clearTimeout(timer)
    }, [landed])

    const setOpen = useCallback(
        (next: boolean) => {
            setExpanded(next)
            onExpandedChange?.(next)
        },
        [onExpandedChange],
    )

    // Focus follows the fold, after the render that reveals the target: focusing an element still
    // under `display: none` is a no-op, so neither call can happen inside the handler.
    useEffect(() => {
        if (expanded) {
            inputRef.current?.focus()
            return
        }
        if (!returnFocusRef.current) return
        returnFocusRef.current = false
        glyphRef.current?.focus()
    }, [expanded])

    const collapse = useCallback(
        (returnFocus: boolean) => {
            returnFocusRef.current = returnFocus
            setOpen(false)
        },
        [setOpen],
    )

    // The keys that act on the tokens, from the words or from a token button.
    const tokenKey = (e: React.KeyboardEvent, atStart: boolean): boolean => {
        const act = e.nativeEvent.isComposing
            ? null
            : pillKeyAction(e.key, atStart, markedIndex < 0 ? null : markedIndex, tokens.length)
        if (!act) return false
        e.preventDefault()
        if (act.kind === 'remove') removeToken(tokens[act.index])
        else setMarked(act.index === null ? null : tokens[act.index].id)
        return true
    }

    const slotClassName = [
        styles.slot,
        folds ? styles.slotFold : null,
        folds && expanded ? styles.slotOpen : null,
    ].filter(Boolean).join(' ')

    const forYou = steer !== undefined && mode === null && lean.length > 0 && yours.length === 0 && !words
    const rowsActive = open && (body === 'rows' || body === 'closest') && shown.length > 0

    // The drag on the half sheet's handle.
    const dragFrom = useRef<number | null>(null)
    const dragged = useRef(false)

    const optionsFor = (list: readonly FieldToken[]) =>
        list.map((token) => (
            <FieldTokenView
                key={token.id}
                token={token}
                marked={marked === token.id}
                onRemove={() => removeToken(token)}
            />
        ))

    return (
        <>
            <div
                className={slotClassName}
                data-open={open ? '' : undefined}
                data-notice={steer?.notice ? '' : undefined}
                // React's onBlur is focusout, so it carries the whole slot's focus moves; a jump from the input to a
                // token or the × inside the slot is not a close.
                onBlur={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
                    setMarked(null)
                    if (steer && mode === 'dropdown') {
                        steer.onCommit(value)
                        closePanel()
                    }
                    if (!expanded || hasQuery) return
                    collapse(false)
                }}
            >
                {folds && (
                    <button
                        type="button"
                        ref={glyphRef}
                        className={hasQuery ? `${styles.glyph} ${styles.glyphLit}` : styles.glyph}
                        aria-expanded={expanded}
                        aria-label={hasQuery ? `${label} (searching: ${value})` : label}
                        onClick={() => setOpen(true)}
                    >
                        <SearchGlyph size={16} />
                    </button>
                )}

                {mode === 'half' && <div className={styles.scrim} role="presentation" onClick={closePanel} />}

                <div
                    className={styles.shell}
                    role="presentation"
                    data-layout={mode ?? undefined}
                    // A press on a button of the field must not take the focus from the input (Safari does not focus
                    // a button on click, and the blur would close the panel under the tap).
                    onMouseDown={(e) => { if ((e.target as Element).closest('button')) e.preventDefault() }}
                    style={mode === 'full' && viewport ? {top: viewport.top, height: viewport.height, bottom: 'auto'} : undefined}
                >
                    <div className={styles.top}>
                        {sheetOpen && (
                            <button type="button" className={styles.back} aria-label="Close search" onClick={() => { inputRef.current?.blur(); closePanel() }}>
                                <svg width="12" height="20" viewBox="0 0 12 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M10 2 L2 10 L10 18" />
                                </svg>
                            </button>
                        )}
                        <FieldForm form={form}>
                            <div
                                role="presentation"
                                className={[styles.field, ringing ? (landed % 2 ? styles.ringOdd : styles.ringEven) : null].filter(Boolean).join(' ')}
                                onClick={(e) => { if (e.target === e.currentTarget) inputRef.current?.focus() }}
                            >
                                <SearchGlyph />
                                {forYou && <span className={styles.forYou}>for you</span>}
                                <div
                                    ref={trackRef}
                                    className={styles.track}
                                    role="presentation"
                                    data-fade-left={edges.left ? '' : undefined}
                                    data-fade-right={edges.right ? '' : undefined}
                                    onScroll={readEdges}
                                    onKeyDown={(e) => {
                                        if (e.target !== inputRef.current && tokenKey(e, true)) inputRef.current?.focus()
                                    }}
                                >
                                    {optionsFor(yours)}
                                    <span className={styles.leanGroup}>{optionsFor(lean)}</span>
                                    <span
                                        className={styles.inputWrap}
                                        data-tokens={tokens.length > 0 ? '' : undefined}
                                        data-yours={yours.length > 0 ? '' : undefined}
                                    >
                                        <input
                                            type="text"
                                            ref={inputRef}
                                            value={words}
                                            onChange={(e) => {
                                                setMarked(null)
                                                onChange(composeQuery(parsed.pills, e.target.value, parsed.facets))
                                            }}
                                            onFocus={openPanel}
                                            onKeyDown={(e) => {
                                                const atStart = e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 0
                                                if (tokenKey(e, atStart)) return
                                                if (e.nativeEvent.isComposing) return
                                                if (rowsActive && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                                                    e.preventDefault()
                                                    const down = e.key === 'ArrowDown'
                                                    setCurrent((c) => (c < 0 ? (down ? 0 : shown.length - 1) : (c + (down ? 1 : shown.length - 1)) % shown.length))
                                                    return
                                                }
                                                // Enter/Go: with a panel it takes the current row, makes (⌘/Ctrl+Enter at any length,
                                                // Enter alone at request length), or only blurs the field; without one it is
                                                // the press that blurs the field and hands the value to `onSubmit` (board
                                                // `FirstGlance`, frame 5 withdrawn 2026-09-18). `isBlurringEnter` states that rule.
                                                if (isBlurringEnter(e.key, false)) {
                                                    e.preventDefault()
                                                    if (steer) {
                                                        const row = rowsActive && rowsFresh ? shown[current] : undefined
                                                        const action = enterAction(e, make, row !== undefined)
                                                        if (action === 'make') makeNow(viaOf(e))
                                                        else if (action === 'take' && row) pickRow(row)
                                                        else {
                                                            e.currentTarget.blur()
                                                            steer.onCommit(value)
                                                            closePanel()
                                                        }
                                                        return
                                                    }
                                                    e.currentTarget.blur()
                                                    onSubmit?.(value)
                                                    return
                                                }
                                                if (e.key === 'Escape' && open) {
                                                    // Escape closes the panel and nothing else.
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    e.currentTarget.blur()
                                                    closePanel()
                                                    return
                                                }
                                                if (e.key !== 'Escape' || !expanded) return
                                                // Escape folds the field and nothing else. A layer that traps focus (the
                                                // column, the sheet, the sign-in card) cannot be open while the caret is
                                                // here — `useFocusTrap` pulls focus back into its layer — so there is
                                                // nothing behind this field for the key to close.
                                                e.preventDefault()
                                                e.stopPropagation()
                                                collapse(true)
                                            }}
                                            placeholder=" "
                                            enterKeyHint={steer ? enterKeyHint(make) : undefined}
                                            className={styles.input}
                                            aria-label={label}
                                            role={steer ? 'combobox' : undefined}
                                            aria-expanded={steer ? open : undefined}
                                            aria-controls={rowsActive ? `${panelId}-rows` : undefined}
                                            aria-autocomplete={steer ? 'list' : undefined}
                                            aria-activedescendant={rowsActive && current >= 0 ? optionId(current) : undefined}
                                            autoComplete="off"
                                            name={form ? 'q' : undefined}
                                            // The typed words become the ghost's direction (BRIEF §4), so the field is
                                            // bounded by the same MAX_DIRECTION_CHARS `POST /continue` enforces.
                                            maxLength={wordsRoom}
                                        />
                                        <span className={styles.hintAnd} aria-hidden="true">and…</span>
                                        <span className={styles.hintQuestion} aria-hidden="true">{placeholder ?? 'Search'}</span>
                                    </span>
                                </div>
                                {folds && (
                                    <button
                                        type="button"
                                        className={styles.close}
                                        aria-label="Close search"
                                        onClick={() => collapse(true)}
                                    >
                                        {CROSS}
                                    </button>
                                )}
                                {showClear && (
                                    <button
                                        type="button"
                                        className={styles.clear}
                                        aria-label={parsed.pills.length + parsed.facets.length > 0 ? 'Clear pills and words' : 'Clear search'}
                                        onClick={() => { setMarked(null); onChange('') }}
                                    >
                                        {CROSS}
                                    </button>
                                )}
                            </div>
                        </FieldForm>
                    </div>

                    {steer?.notice && (
                        <div className={styles.undo}>
                            <Notice kind="info" ttlMs={0}>{steer.notice.line}</Notice>
                            <button type="button" className={styles.undoAction} onClick={steer.notice.onUndo}>Undo</button>
                        </div>
                    )}

                    {open && steer && mode && (
                        // A press inside the panel must not take the focus from the field.
                        <div role="presentation" onMouseDown={(e) => e.preventDefault()} style={{display: 'contents'}}>
                            <SearchPanel
                                id={panelId}
                                layout={mode}
                                body={body}
                                facets={steer.facets}
                                shares={steer.shares}
                                yours={yours}
                                lean={lean}
                                facetOffer={steer.facetOffer}
                                words={words}
                                recent={steer.recent}
                                offered={steer.withThese}
                                rows={rows}
                                current={current}
                                make={make}
                                optionId={optionId}
                                onClearRecent={steer.onRecentClear}
                                onRemoveRecent={steer.onRecentRemove}
                                onPickRecent={(q) => land('recent', q)}
                                onPickFacet={(token) => land('facet', withToken(value, {type: 'facet', value: token}))}
                                onPickWith={(phrase) => land('with', withToken(value, {type: 'phrase', value: phrase}))}
                                onPickRow={pickRow}
                                onHoverRow={setCurrent}
                                onMake={() => makeNow('make_row')}
                            />
                            {mode === 'full' && rowsActive && (
                                <div className={styles.strip}>
                                    {shown.slice(0, 3).map((row) => (
                                        <OfferedChip key={`${row.kind}:${row.pill}`} label={row.label} onClick={() => pickRow(row)} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {mode === 'half' && (
                        <button
                            type="button"
                            className={styles.grab}
                            aria-label="Drag down to close, up for the full panel"
                            onClick={() => {
                                // A drag ends in a click too; only a plain tap closes.
                                if (dragged.current) dragged.current = false
                                else closePanel()
                            }}
                            onPointerDown={(e) => {
                                dragFrom.current = e.clientY
                                e.currentTarget.setPointerCapture(e.pointerId)
                            }}
                            onPointerUp={(e) => {
                                const from = dragFrom.current
                                dragFrom.current = null
                                if (from === null) return
                                const outcome = dragOutcome(e.clientY - from)
                                dragged.current = outcome !== 'stay'
                                if (outcome === 'close') closePanel()
                                else if (outcome === 'expand') { setMode('full'); inputRef.current?.focus() }
                            }}
                        />
                    )}
                </div>
            </div>

            {steer && lean.length > 0 && (
                <div
                    className={styles.leanLine}
                    role="presentation"
                    data-hidden={mode !== null ? '' : undefined}
                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setMarked(null) }}
                    onKeyDown={(e) => { tokenKey(e, true) }}
                >
                    <span className={styles.lineLabel}>for you</span>
                    {optionsFor(lean)}
                </div>
            )}
        </>
    )
}

function FieldForm({form, children}: {form: HeaderSearchProps['form']; children: React.ReactNode}) {
    if (!form) return <>{children}</>
    return (
        <form action={form.action} method="get" rel={form.rel} style={{display: 'contents'}}>
            {Object.entries(form.hidden).map(([name, value]) => (
                <input key={name} type="hidden" name={name} value={value} />
            ))}
            {children}
        </form>
    )
}

function SearchGlyph({size = 14}: {size?: number}) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M16.6 16.6 L21 21" />
        </svg>
    )
}
